import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import CustomLoader from "../custom/CustomLoader";
import PaginationControl from "../custom/PaginationControl";
import moment from "moment";
import { useFetch, useMutate } from "../../hooks/queries";
import { FaDownload } from "react-icons/fa";

const CoursesDataModal = ({
  show,
  onHide,
  statusFilter, // "published", "unpublished", or "all"
  startDate,
  endDate,
  schoolId,
  operatorId,
}) => {
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Reset page when modal opens or filters change
  useEffect(() => {
    if (show) {
      setPage(1);
    }
  }, [show, statusFilter, startDate, endDate, schoolId, operatorId]);

  const handleSort = (column, sortDirection) => {
    const columnFieldMap = {
      courseId: "CourseId",
      courseName: "CourseName",
      operatorName: "OperatorName",
      schoolName: "SchoolName",
      term: "Term",
      students: "Students",
      status: "Status",
    };
    setSortBy(columnFieldMap[column.id]);
    setSortOrder(sortDirection);
    setPage(1);
  };

  const buildRequestBody = () => {
    const ot = localStorage.getItem("OTsReD");
    const st = localStorage.getItem("STsReD");

    const safeParseInt = (value) => {
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parsedOt = safeParseInt(ot);
    const parsedSt = safeParseInt(st);

    const data = {
      OperatorId: 0,
      SchoolId: 0,
      ...(statusFilter !== "all" && {
        StartDate: startDate,
        EndDate: endDate,
      }),
      PageNumber: page,
      PageSize: perPage,
      studentsLastLoginFilter: [],
      SearchText: "",
      SortBy: sortBy,
      SortOrder: sortOrder,
    };

    // Add IsPublished filter if not showing all courses
    if (statusFilter !== "all") {
      data.IsPublished = statusFilter === "published";
    }

    if (parsedOt !== 0 && parsedSt === 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = safeParseInt(schoolId);
    } else if (parsedOt !== 0 && parsedSt !== 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = parsedSt;
    } else {
      data.OperatorId = safeParseInt(operatorId);
      data.SchoolId = safeParseInt(schoolId);
    }

    return data;
  };

  //edit here

  const {
    data: coursesResponse,
    isPending,
    isLoading,
    isFetching,
  } = useFetch({
    queryKey: [
      "coursesModal",
      page,
      perPage,
      startDate,
      endDate,
      schoolId,
      operatorId,
      sortBy,
      sortOrder,
      statusFilter,
    ],
    url: `${process.env.REACT_APP_API_URL}Courses/GetCourses/`,
    method: "PATCH",
    data: buildRequestBody(),
    enabled: show,
  });

  const courses = coursesResponse?.data || [];
  const totalRows = coursesResponse?.totalCount || 0;
  const isLoading_ = isPending || isLoading || isFetching;

  const columns = [
    {
      id: "courseId",
      name: "Course Id",
      selector: (row) => row.courseId,
      sortable: true,
      width: "130px",
    },
    {
      id: "courseName",
      name: "Course Name",
      selector: (row) => row.courseName,
      sortable: true,
    },
    {
      id: "operatorName",
      name: "Operator",
      selector: (row) => (row.operatorName == null ? "N/A" : row.operatorName),
      sortable: true,
      width: "150px",
    },
    {
      id: "schoolName",
      name: "School",
      selector: (row) => (row.schoolName == null ? "N/A" : row.schoolName),
      sortable: true,
      width: "180px",
    },
    {
      id: "term",
      name: "Term",
      selector: (row) => row.term,
      sortable: true,
      width: "100px",
    },
    {
      id: "students",
      name: "Total Students",
      selector: (row) => row.students,
      sortable: true,
      width: "130px",
    },
    {
      id: "status",
      name: "Status",
      selector: (row) =>
        row.status === "available"
          ? "Published"
          : row.status === "completed"
            ? "Completed"
            : "Unpublished",
      sortable: true,
      width: "120px",
    },
  ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const buildExportPayload = () => {
    return {
      startDate: startDate,
      endDate: endDate,
      schoolId: schoolId == null ? 0 : schoolId,
      operatorId: operatorId == null ? 0 : operatorId,
      isPublished:
        statusFilter === "published"
          ? true
          : statusFilter === "unpublished"
            ? false
            : null,
    };
  };

  const downloadFile = (data, fileName) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.download = fileName;
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportMutation = useMutate({
    mutationKey: ["exportCoursesModal"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/ExportCourses`,
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      downloadFile(data, `${modalTitle}.xlsx`);
    },
  });

  const exporting = exportMutation.isPending || exportMutation.isLoading;

  const modalTitle =
    statusFilter === "published"
      ? "Courses - Published"
      : statusFilter === "unpublished"
        ? "Courses - Unpublished"
        : "Courses - All";

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header
        style={{
          backgroundColor: "#287a9f",
          color: "white",
          borderRadius: "0",
        }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: "bold" }}>
          {modalTitle}
        </Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={onHide}
          aria-label="Close"
        ></button>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "15px", borderBottom: "1px solid #ddd" }}>
          <button
            className="btn cts-btn-primary-3 btn-sm"
            onClick={() => exportMutation.mutate(buildExportPayload())}
            disabled={exporting || isLoading_}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FaDownload />
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
        <DataTable
          data={courses}
          columns={columns}
          progressPending={isLoading_}
          progressComponent={<CustomLoader />}
          onSort={handleSort}
          sortServer
          fixedHeader
          fixedHeaderScrollHeight={"50vh"}
          defaultSortFieldId={1}
          noDataComponent={
            <div style={{ padding: "24px", textAlign: "center" }}>
              No records found
            </div>
          }
        />
        <PaginationControl
          totalCount={totalRows}
          currentPage={page}
          pageSize={perPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePerRowsChange}
          pageSizeOptions={[20, 50, 100, 200]}
          disabled={isLoading_}
        />
      </Modal.Body>
    </Modal>
  );
};

export default CoursesDataModal;
